import { BookText, X } from '@/components/icons/barrel';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UnitSelect } from '@/features/trade/order/components/unit-select';
import useSymbol from '@/hooks/use-symbol';
import { cn } from '@/lib/utils';
import { COLLATERAL_SYMBOL, useMarketStore } from '@/store/use-markets-store';
import { useWebSocketStore } from '@/store/use-websocket-store';
import { ActivityTab, WsType } from '@/types';
import { abbrFromSym } from '@/utils/token-symbol';
import { CaretSortIcon } from '@radix-ui/react-icons';
import { Icon } from '@radix-ui/react-select';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { useTrades } from '../api/get-trades';
import OrderBook from './order-book';
import Trades from './trades';

export default function Activity() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const symbol = useSymbol();
  const [unit, setUnit] = useState(abbrFromSym(symbol));
  const [activeTab, setActiveTab] = useState(ActivityTab.DEPTH);

  const { hasPublicWs, isConnected } = useWebSocketStore(
    useShallow((state) => ({
      hasPublicWs: !!state.publicWs,
      isConnected: state.isConnected[WsType.PUBLIC],
    })),
  );

  const { isLoading: areTradesLoading } = useTrades({ symbol });

  useEffect(() => {
    if (!hasPublicWs) return;
    const { publicWs } = useWebSocketStore.getState();
    if (
      !publicWs ||
      areTradesLoading ||
      !isConnected ||
      activeTab !== ActivityTab.TRADES
    )
      return;
    publicWs.subscribeToChannels([`${symbol}@trades`]);
    return () => {
      publicWs.unsubscribeFromChannels([`${symbol}@trades`]);
    };
  }, [symbol, hasPublicWs, areTradesLoading, isConnected, activeTab]);

  useEffect(() => {
    if (unit === COLLATERAL_SYMBOL || unit === abbrFromSym(symbol)) return;
    setUnit(abbrFromSym(symbol));
  }, [symbol, unit]);

  const { tickSizes, defaultTickSize } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      tickSizes: state.marketSpec.tickSizes,
      defaultTickSize: state.marketSpec.defaultTickSize,
    })),
  );

  const [tickSize, setTickSize] = useState(defaultTickSize || tickSizes[0]);

  useEffect(() => {
    setTickSize(defaultTickSize || tickSizes[0]);
  }, [defaultTickSize, tickSizes]);

  useEffect(() => {
    useMarketStore(symbol)
      .getState()
      .setActiveTickSize(tickSize ?? null);

    return () => {
      useMarketStore(symbol).getState().setActiveTickSize(null);
    };
  }, [symbol, tickSize]);

  const isOpenReady = useStore(
    useMarketStore(symbol),
    (state) =>
      tickSize in state.books &&
      Object.values(state.books[tickSize]).every(
        (v: [bigint, bigint][]) => v.length > 0,
      ),
  );

  useEffect(() => {
    if (!isOpenReady) return;
    setIsOpen(true);
  }, [isOpenReady]);

  useEffect(() => {
    if (!hasPublicWs) return;
    const { publicWs } = useWebSocketStore.getState();
    if (!publicWs || !isConnected || activeTab !== ActivityTab.DEPTH) return;
    if (tickSize === defaultTickSize) return;
    const channel = `${symbol}@depth_${tickSize}`;
    publicWs.subscribeToChannels([channel]);
    return () => publicWs.unsubscribeFromChannels([channel]);
  }, [hasPublicWs, symbol, tickSize, isConnected, activeTab, defaultTickSize]);

  return (
    <Tabs
      value={activeTab}
      onValueChange={(tab) => setActiveTab(tab as ActivityTab)}
      className={cn(
        'flex w-full shrink-0 flex-col border-l border-border transition-all',
        isOpen ? 'max-w-xs' : 'max-w-12 overflow-hidden',
      )}
    >
      {isOpen ? (
        <>
          <TabsList className="flex h-[38px] items-end justify-end">
            <div className="grid flex-1 grid-cols-2 items-end">
              <TabsTrigger
                value={ActivityTab.DEPTH}
                className="h-full w-full border-t-0 px-4"
              >
                {t('activity.depth')}
              </TabsTrigger>
              <TabsTrigger
                value={ActivityTab.TRADES}
                className="h-full flex-1 border-t-0 px-4"
              >
                {t('activity.trades')}
              </TabsTrigger>
            </div>

            <button
              className="h-full shrink-0 border-b border-border p-3"
              onClick={() => setIsOpen((prev) => !prev)}
            >
              <X size={14} />
            </button>
          </TabsList>
          <div className="grid grid-cols-2">
            <Select
              value={tickSize}
              onValueChange={(size) => setTickSize(size)}
            >
              <SelectTrigger className="h-full rounded-none border-0 border-b border-r text-base hover:bg-vestgrey-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent
                sideOffset={-4}
                alignOffset={-1}
                className="bg-background [&>div]:p-0"
              >
                {tickSizes.map((size) => (
                  <SelectItem
                    className="h-auto border-b border-border bg-background px-3 py-1.5 text-base last:border-b-0 focus:bg-vestgrey-800"
                    value={size}
                    key={size}
                  >
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <UnitSelect
              defaultValue={abbrFromSym(symbol)}
              className="h-full border-b border-border [&>button]:h-full [&>button]:bg-background [&>button]:px-3 hover:[&>button]:!bg-vestgrey-800"
              units={[COLLATERAL_SYMBOL, abbrFromSym(symbol)]}
              onSelect={(value) => setUnit(value)}
              icon={
                <Icon asChild>
                  <CaretSortIcon className="h-4 w-4 opacity-50" />
                </Icon>
              }
            />
          </div>
          <TabsContent className="relative flex-1" value={ActivityTab.DEPTH}>
            <OrderBook unit={unit} tickSize={tickSize} />
          </TabsContent>
          <TabsContent className="relative flex-1" value={ActivityTab.TRADES}>
            <Trades unit={unit} />
          </TabsContent>
        </>
      ) : (
        <button
          className="mx-auto w-full max-w-max py-3"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <BookText size={14} />
        </button>
      )}
    </Tabs>
  );
}
